import { useState } from 'react'

import { useLazyQuery } from '@apollo/client'
import { ActionIcon, Box, Divider, Menu, TextInput } from '@mantine/core'

import { toast } from 'src/components/Overlays/Toast/Toast'
import { GET_JOB_LISTINGS } from 'src/graphql/joblistings.graphql'
import IconChevronDown from 'src/icons/IconChevronDown'
import IconChevronLeft from 'src/icons/IconChevronLeft'
import { formatSnakeValue } from 'src/lib/formatters'

const JOB_LISTINGS_PAGE_LIMIT = 300

const onError = () => {
  toast('Unable to load job listings', 'error')
}

const JobListingsMenu = ({ handleJobListingChange }) => {
  const [search, setSearch] = useState('')
  const [selectedJobListing, setSelectedJobListing] = useState(null)

  const [
    loadJobListings,
    { data: jobListingsData, loading: loadingJobListings },
  ] = useLazyQuery(GET_JOB_LISTINGS, {
    onError,
  })
  const jobListings = jobListingsData?.jobListings?.items || []

  return (
    <Menu
      shadow="md"
      width={300}
      position="bottom-end"
      // closeOnItemClick={false}
      onOpen={() => {
        if (!jobListingsData) {
          loadJobListings({
            variables: {
              pageInfo: { offset: 0, limit: JOB_LISTINGS_PAGE_LIMIT },
            },
          })
        }
      }}
    >
      <Menu.Target>
        <ActionIcon variant="subtle" color="gray" size={16}>
          <IconChevronDown className="h-4 w-4 fill-doubleNickel-white stroke-doubleNickel-gray-700" />
        </ActionIcon>
      </Menu.Target>

      <Menu.Dropdown className="flex max-h-[300px] flex-col overflow-y-auto p-2">
        {!selectedJobListing ? (
          <>
            <Menu.Label className="px-0 font-medium text-doubleNickel-gray-800">
              Job Listings
            </Menu.Label>
            <Divider />
            <TextInput
              placeholder="Search job listings"
              className="my-2"
              value={search}
              size="xs"
              variant="filled"
              onChange={(e) => setSearch(e.target.value)}
            />
            {jobListings
              .filter((jobListing) => jobListing.status === 'ACTIVE')
              .filter((jobListing) =>
                jobListing.title.toLowerCase().includes(search.toLowerCase())
              )
              .map((jobListing) => (
                <div key={jobListing.jobListingId}>
                  <Box
                    className="cursor-pointer p-2 text-xs text-doubleNickel-gray-600 hover:bg-doubleNickel-gray-100"
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      setSelectedJobListing(jobListing)
                    }}
                  >
                    {jobListing.title}
                  </Box>
                </div>
              ))}
          </>
        ) : (
          <>
            <Menu.Label className="text-xs font-medium text-doubleNickel-gray-900">
              <div className="flex flex-row items-center gap-2">
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size={16}
                  onClick={() => setSelectedJobListing(null)}
                >
                  <IconChevronLeft className="h-3 w-3 fill-none stroke-doubleNickel-gray-600" />
                </ActionIcon>
                {selectedJobListing.title}
              </div>
            </Menu.Label>
            <Divider className="my-2" />
            {selectedJobListing.trackingLinks &&
              selectedJobListing.trackingLinks.length > 0 && (
                <>
                  {selectedJobListing.trackingLinks.map((trackingLink) => (
                    <Menu.Item
                      key={trackingLink.trackingLinkId}
                      className="text-xs text-doubleNickel-gray-600"
                      onClick={() => {
                        handleJobListingChange(
                          selectedJobListing.jobListingId,
                          trackingLink.trackingLinkId
                        )
                      }}
                    >
                      {trackingLink.linkName && `${trackingLink.linkName} · `}
                      {formatSnakeValue(trackingLink.linkType)}
                    </Menu.Item>
                  ))}
                </>
              )}
          </>
        )}

        {jobListings.length === 0 && !loadingJobListings && (
          <Menu.Item disabled>No job listings available</Menu.Item>
        )}
      </Menu.Dropdown>
    </Menu>
  )
}

export default JobListingsMenu
